import { observer } from "mobx-react"
import { Button, Col, Row, Typography, Modal, Form, Select, Upload, message } from "antd"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { CompanyExcelViewModel } from "./CompanyExcelViewModel"
import { Card } from "../../styledComponents/CustomCard/Card"
import { useTranslation } from "react-i18next"
import { UploadOutlined, PlusOutlined } from "@ant-design/icons"
import CompanyExcelTable from "./components/CompanyExcelTable"
import { MicrositesViewModel } from "../microSites/MicrositesViewModel"
import { AuthStore } from "../../stores/AuthStore"
import { useState } from "react"
import type { RcFile } from "antd/es/upload/interface"

const { Title } = Typography

const CompanyExcelView = () => {
	const { t } = useTranslation("companyExcel")
	const viewModel = useContainerInjection<CompanyExcelViewModel>("CompanyExcelViewModel")
	const micrositesViewModel = useContainerInjection<MicrositesViewModel>("MicrositesViewModel")
	const { userData } = useContainerInjection<AuthStore>("AuthStore")
	const [form] = Form.useForm()
	const [file, setFile] = useState<File | null>(null)

	const handleSubmit = async (values: any) => {
		if (!file) {
			message.error(t("uploadModal.selectFile"))
			return
		}

		if (!userData?.id) {
			message.error("User ID not found")
			return
		}

		try {
			const result = await viewModel.uploadCompanyExcel({
				file: file,
				micrositeId: values.micrositeId,
				userId: userData.id
			})

			if (result.success) {
				message.success(result.message)
				form.resetFields()
				setFile(null)
				viewModel.setModalVisible(false)
			} else {
				message.error(result.message)
			}
		} catch (error) {
			message.error(t("uploadModal.error"))
		}
	}

	const handleCancel = () => {
		viewModel.setModalVisible(false)
		form.resetFields()
		setFile(null)
	}

	return (
		<>
			<div className="tabled">
				<Row gutter={[24, 0]}>
					<Col xs={24} xl={24}>
						<Card
							bordered={false}
							className="criclebox tablespace mb-24"
							title={
								<div className="flex items-center justify-between">
									<Title className="mt-3" level={4}>
										{t("title")}
									</Title>
									<Button
										type="primary"
										icon={<PlusOutlined />}
										onClick={() => viewModel.setModalVisible(true)}
									>
										{t("uploadButton")}
									</Button>
								</div>
							}
						>
							<div className="table-responsive">
								<CompanyExcelTable data={viewModel.companyExcels} loading={viewModel.isLoading} />
							</div>
						</Card>
					</Col>
				</Row>
			</div>

			<Modal
				title={t("uploadModal.title")}
				open={viewModel.isModalVisible}
				onCancel={handleCancel}
				footer={null}
				width={600}
			>
				<Form form={form} layout="vertical" onFinish={handleSubmit}>
					<Form.Item
						name="micrositeId"
						label={t("uploadModal.microsite")}
						rules={[{ required: true, message: t("uploadModal.micrositeRequired") }]}
					>
						<Select
							showSearch
							placeholder={t("uploadModal.selectMicrosite")}
							loading={micrositesViewModel.isLoading}
							filterOption={(input, option) =>
								option?.label && typeof option.label === "string"
									? option.label.toLowerCase().includes(input.toLowerCase())
									: false
							}
							options={micrositesViewModel.microsites.map(microsite => ({
								label: `${microsite.id} - ${microsite.slug}`,
								value: microsite.id
							}))}
						/>
					</Form.Item>

					<Form.Item label={t("uploadModal.file")}>
						<Upload
							accept=".xlsx,.xls"
							maxCount={1}
							fileList={file ? [{ uid: "-1", name: file.name, status: "done" }] : []}
							beforeUpload={(uploadFile: RcFile) => {
								setFile(uploadFile as File)
								return false
							}}
							onRemove={() => {
								setFile(null)
							}}
						>
							<Button icon={<UploadOutlined />}>{t("uploadModal.selectFile")}</Button>
						</Upload>
					</Form.Item>

					<Form.Item className="flex justify-end">
						<Button onClick={handleCancel} style={{ marginRight: 8 }}>
							{t("uploadModal.cancel")}
						</Button>
						<Button type="primary" htmlType="submit" loading={viewModel.isUploading}>
							{t("uploadModal.upload")}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</>
	)
}

export default observer(CompanyExcelView)
